<template>
  <!--
    장비 설치 신청 직접 추가 창(설계안 §9.7, 10-23 사용자 요청) — 직원이 매장을 골라 POS 신청과 같은 신청을 넣는다. 매장을 고르면
    미리 채움을 읽어 매장·대표자(읽기만)와 설치 주소·연락받을 휴대폰(고칠 수 있다)을 채운다. 알림톡(사장님 접수·담당자 새 신청)은 서버가
    POS 신청과 똑같이 보낸다. 판정·문구·요청은 `utils/equipmentRequest` 한 곳 — 창은 조회·쓰기와 그리기만.
  -->
  <el-dialog
    :title="$t(text.addTitle)"
    :visible="visible"
    width="480px"
    top="6vh"
    append-to-body
    data-testid="equipment-add-dialog"
    :close-on-click-modal="false"
    @update:visible="$emit('update:visible', $event)"
    @open="reset">
    <!-- 1024×768 에서 [추가]까지 한 화면에 — small 칸, 읽기만인 대표자는 매장 줄에 -->
    <el-form label-position="top" size="small" @submit.native.prevent>
      <el-form-item :label="$t(text.columnStore)" :error="problems.storeCd">
        <!--
          창이 열린 뒤에만 그린다(선택기가 그리자마자 범위 트리를 읽는다). 값은 고른 이벤트(`change`)로만 받는다 — 폴백 입력 칸이 빈 값을
          1 로 채워 보이는데 그 1 이 고른 매장이 되면 안 된다(`SubscriptionGrantDialog` 와 같은 쓰임)
        -->
        <StorePicker
          v-if="visible"
          :value="storeCd"
          fallback="input"
          :auto-select="false"
          data-testid="equipment-add-picker"
          @change="pickStore" />
        <div v-if="prefill" class="equipment-add__store" data-testid="equipment-add-store">
          {{ prefill.storeNm }} · {{ prefill.storeId }} · {{ $t(text.detailCeo) }}
          <span data-testid="equipment-add-ceo">{{ prefill.ceoNm || '—' }}</span>
        </div>
      </el-form-item>
      <template v-if="prefill">
        <p v-if="openText" class="equipment-add__open" data-testid="equipment-add-open">{{ openText }}</p>
        <template v-else>
          <el-form-item :label="$t(text.detailAddress)" :error="problems.address">
            <el-input v-model="form.address" data-testid="equipment-add-address"></el-input>
          </el-form-item>
          <el-form-item :label="$t(text.addContact)" :error="problems.contactTel">
            <el-input
              v-keep-number-caret
              :value="util.telFieldText(form.contactTel)"
              data-testid="equipment-add-contact"
              :placeholder="contactPlaceholder"
              @input="form.contactTel = util.telDigits($event)"></el-input>
          </el-form-item>
          <el-form-item :label="$t(text.columnMemo)" :error="problems.memo">
            <el-input
              v-model="form.memo"
              type="textarea"
              :rows="2"
              data-testid="equipment-add-memo"
              :placeholder="$t(text.addMemoPlaceholder)"></el-input>
          </el-form-item>
          <p class="equipment-add__notice" data-testid="equipment-add-notice">{{ $t(text.addNotice) }}</p>
        </template>
      </template>
    </el-form>
    <p v-if="problem" class="equipment-add__problem" data-testid="equipment-add-problem">{{ problem }}</p>
    <span slot="footer">
      <el-button size="small" @click="$emit('update:visible', false)">{{ $t(text.addCancel) }}</el-button>
      <el-button
        size="small"
        type="primary"
        data-testid="equipment-add-submit"
        :disabled="!canSubmit"
        :loading="busy"
        @click="submit"
        >{{ $t(text.addSubmit) }}</el-button
      >
    </span>
  </el-dialog>
</template>

<script>
import StorePicker from '@/components/store/StorePicker.vue'
import { httpErrorMessage } from '@/plugins/http/policy'
import {
  EQUIPMENT_PREFILL_API,
  EQUIPMENT_STEP_REQUEST_CONFIG,
  EQUIPMENT_TEXT,
  addDraftOf,
  addRequestOf,
  contactPlaceholderOf,
  isRequestOpen,
  openRequestTextOf,
} from '@/utils/equipmentRequest'
import { isStoreCd } from '@/utils/subscriptionAdmin'

const noProblems = () => ({ storeCd: '', address: '', contactTel: '', memo: '' })

export default {
  name: 'EquipmentRequestAddDialog',

  components: { StorePicker },

  props: {
    visible: { type: Boolean, default: false },
  },

  data: function () {
    return {
      text: EQUIPMENT_TEXT,
      /** 선택기에서 고른 매장 */
      storeCd: null,
      /** 고른 매장의 미리 채움(`GET …/prefill`) — 읽기 전에는 `null`(칸을 그리지 않는다) */
      prefill: null,
      form: addDraftOf(null),
      problems: noProblems(),
      /** 칸이 아닌 실패(서버 문장) */
      problem: '',
      busy: false,
      /** 이 창의 신청 하나 — 두 번 눌러도·응답을 못 받고 다시 눌러도 신청은 하나다(서버 멱등) */
      idempotencyKey: '',
      /** 미리 채움을 받은 때 — 열린 신청의 「오늘·어제」를 이 순간으로 말한다 */
      nowMs: Date.now(),
    }
  },

  computed: {
    /** 그 매장의 열린 신청 한 줄 — 있으면 [추가]를 막는다 */
    openText: function () {
      return openRequestTextOf(this.prefill && this.prefill.open, this.nowMs)
    },
    contactPlaceholder: function () {
      return this.$t(contactPlaceholderOf(this.prefill))
    },
    canSubmit: function () {
      return !!this.prefill && !this.openText
    },
  },

  methods: {
    /** 열 때마다 새로 — 매장을 비우고 이 창의 신청 키를 새로 만든다(멱등 키 = 이 창에서 넣는 신청 하나) */
    reset: function () {
      this.storeCd = null
      this.prefill = null
      this.form = addDraftOf(null)
      this.problems = noProblems()
      this.problem = ''
      this.idempotencyKey = window.crypto.randomUUID()
    },

    /** 선택기에서 골랐다 — 그 매장의 미리 채움을 읽는다(비우면 칸을 거둔다) */
    pickStore: async function (storeCd) {
      this.storeCd = storeCd
      this.prefill = null
      this.problems = noProblems()
      this.problem = ''
      if (!isStoreCd(storeCd)) return

      try {
        const response = await this.$axios.get(
          EQUIPMENT_PREFILL_API,
          Object.assign({ params: { storeCd } }, EQUIPMENT_STEP_REQUEST_CONFIG)
        )

        this.showPrefill(response.data)
        this.form = addDraftOf(response.data)
      } catch (error) {
        this.problem = httpErrorMessage(error, this.$t(this.text.prefillFailed))
      }
    },

    showPrefill: function (prefill) {
      this.prefill = prefill
      this.nowMs = Date.now()
    },

    /**
     * 추가 — 201 이면 창을 닫고 부모가 목록·위 카드를 다시 읽는다. 409 `REQUEST_OPEN` 은 그 사이 그 매장에 신청이 생긴 것이다 — 그
     * 신청을 열린 신청 줄로 보이고(같은 말) 목록도 다시 읽는다. 그 밖의 실패는 이 창 안에서 한 번 말한다
     */
    submit: async function () {
      const request = addRequestOf(this.prefill, this.form, this.idempotencyKey)
      this.problems = noProblems()
      this.problem = ''
      if (!request.ok) {
        this.problems = Object.assign(noProblems(), { [request.field]: this.$t(request.problem) })
        return
      }

      this.busy = true
      try {
        await this.$axios.post(request.url, request.body, EQUIPMENT_STEP_REQUEST_CONFIG)

        this.$message({ type: 'success', message: this.$t(this.text.addDone) })
        this.$emit('update:visible', false)
        this.$emit('changed')
      } catch (error) {
        if (!isRequestOpen(error)) {
          this.problem = httpErrorMessage(error, this.$t(this.text.addFailed))
          return
        }

        this.showPrefill(Object.assign({}, this.prefill, { open: error.data.request }))
        this.$emit('changed')
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.equipment-add__store {
  font-size: 12px;
  line-height: 20px;
  color: var(--color-text-secondary);
}

.equipment-add__open {
  margin: 0 0 12px;
  font-weight: 500;
  color: var(--color-danger);
  word-break: keep-all;
}

.equipment-add__notice {
  margin: 0;
  color: var(--color-text-regular);
  word-break: keep-all;
}

.equipment-add__problem {
  margin: 12px 0 0;
  color: var(--color-danger);
}
</style>
